import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';

import { InputBase } from '@material-ui/core';

import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import { DatePicker } from '@material-ui/pickers';

import { Paper, Grid } from '@material-ui/core';

import moment from 'moment-timezone';

import { useTranslation, toast } from '@common/utils';
import { useSettings } from '@common/hooks';
import http from '@common/http';
import customLocales from '@common/customMomentLocales';

const customLocale = {
	en: moment.localeData('en-gb'),
	lt: customLocales.lt,
	pl: customLocales.pl,
};


function DateComponent(pickerProps, boxes) {
	const classes = useStyles();

	const { format, inputRef, onClick, onKeyDown, placeholder, value } = pickerProps;
	const props = { format, inputRef, onClick, onKeyDown, value };

	if (boxes) {
		return (
			<label className={classes.box}>
				<div className={classes.label}>{placeholder}</div>
				<InputBase {...props} className={classes.date} placeholder={boxes ? '-' : placeholder} readOnly />
			</label>
		);
	}

	return (
		<InputBase {...props} className={classes.input} placeholder={placeholder} />
	);
}


const getAvailabilityDates = async (from, to, productId, rangeFrom) => {
	const FROM = moment(from).toISOString();
	const TO = moment(to).toISOString();
	const RANGE_FROM = moment(rangeFrom).toISOString();

	const body = productId ? { from: FROM, to: TO, products: [productId], rangeFrom: RANGE_FROM } : { from: FROM, to: TO };

	const { data } = await http.post('/api/kiosks/coliving/products/availability', body);

	return data;
};

function DatePickers({ dates, setDates, boxes, productId, minDuration }) {
	const classes = useStyles();
	const tr = useTranslation();
	const { locale } = useSelector(state => state.locale);
	moment.updateLocale(locale, customLocale[locale]);

	const thisMoment = moment();
	const startOfMonth = moment(thisMoment).startOf('month');
	const endOfMonth = moment(thisMoment).endOf('month');

	const anchorElRef = useRef();
	const { reservationDates } = useSettings();
	const [anchorEl, setAnchorEl] = useState();
	const [availabilityDates, setAvailabilityDates] = useState([]);
	const [availabilityRange, setAvailabilityRange] = useState(
		{
			from: startOfMonth,
			to: endOfMonth,
		},
	);

	useEffect(() => {
		setAnchorEl(anchorElRef);
	}, [anchorElRef]);

	useEffect(async () => {
		const FROM = availabilityRange.from;
		const TO = availabilityRange.to;
		const rangeFrom = availabilityRange.rangeFrom;

		try {
			const data = await getAvailabilityDates(FROM, TO, productId, rangeFrom);

			setAvailabilityDates(data);

		} catch (e) {
			setAvailabilityDates([]);

			toast(e);
		}

	}, [availabilityRange]);

	let selected = false;

	const handleDateClose = () => {
		if (!dates.from) {
			selected = false;
		}
	};

	const handleDateOpen = (prop) => {

		if (dates.from !== null && prop === 'from') {
			setAvailabilityRange({
				from: moment(dates.from).startOf('month'),
				to: moment(dates.from).endOf('month'),
				rangeFrom: prop === 'to' ? moment(dates.from) : null,
			});
		} else if (dates.to !== null && prop === 'to') {
			setAvailabilityRange({
				from: moment(dates.to).startOf('month'),
				to: moment(dates.to).endOf('month'),
				rangeFrom: prop === 'to' ? moment(dates.from) : null,
			});
		} else {
			setAvailabilityRange({
				from: availabilityRange?.from || startOfMonth,
				to: availabilityRange?.to || endOfMonth,
				rangeFrom: null,
			});
		}
	};

	const handleDateChange = (prop, datetime) => {
		if ((moment(reservationDates.from).isAfter(moment()) && prop === 'to') && !selected && !dates[prop]) {
			selected = true;
			return;
		}

		let from = prop === 'from' ? moment(datetime).startOf('day') : dates.from ? moment(dates.from).startOf('day') : null;
		let to = prop === 'to' ? moment(datetime).endOf('day') : dates.to ? moment(dates.to).endOf('day') : null;

		if (prop === 'from' && (!to || moment(to).startOf('day').diff(moment(datetime).startOf('day'), 'days') < reservationDates.minDuration)) {
			to = moment(datetime).add(minDuration ? minDuration : reservationDates.minDuration, 'days').endOf('day');
		}

		if (prop === 'to' && (!from || moment(datetime).startOf('day').diff(moment(from).startOf('day'), 'days') < reservationDates.minDuration)) {
			from = moment(datetime).subtract(minDuration ? minDuration : reservationDates.minDuration, 'days').startOf('day');
		}

		setDates({
			...dates,
			from: from.toISOString(),
			to: to.toISOString(),
		});

	};

	const handleMonthChange = (prop, date) => {
		const FROM = moment(date).startOf('month');
		const TO = moment(date).endOf('month');

		setAvailabilityRange({
			from: FROM,
			to: TO,
			rangeFrom: prop === 'to' ? moment(dates.from) : null,
		});

	};

	const getDisabledDates = (prop) => {
		const disabledDays = (availabilityDates || [])
			.filter(d => prop === 'checkIn' ? !d.availableCheckIn : !d.availableCheckOut)
			.map(d => Number(moment(d.day).format('D')));

		return disabledDays || [];
	};

	const getBoolean = (day, checkedDay) => {
		return (
			moment(day).format('YYYY-MM-DD') === moment(checkedDay).format('YYYY-MM-DD')
		);
	};

	function getDayElement(day, selectedDate, isInCurrentMonth, prop) {
		const today = new Date();
		const disabledDays = getDisabledDates(prop);

		const isDisabled = disabledDays.includes(Number(moment(day).format('D')));
		const isSelected = getBoolean(day, selectedDate);
		const isToday = getBoolean(day, today);
		const isPastDays = moment(day).format('YYYY-MM-DD') < moment(today).format('YYYY-MM-DD');

		let specificDays;
		if (isInCurrentMonth) {
			if (isDisabled) {
				specificDays = (
					<div className={isSelected ? classes.specificSelectedDayPaper : {}} >
						<Paper className={isSelected ? classes.selectedDayPaper : isToday ? classes.specificDaysTodayPaper : classes.specificDaysNormalDayPaper}>
							<Grid item>
								<div className={!isSelected ? classes.daysHover : {}}>
									{new Date(day).getDate()}
								</div>
							</Grid>
						</Paper >
					</div>

				);
			} else {
				specificDays = (
					<Paper className={isSelected ? classes.selectedDayPaper : isToday ? classes.todayPaper : classes.normalDayPaper}>
						<div className={!isSelected ? classes.daysHover : {}}>
							<Grid item > {new Date(day).getDate()}</Grid>
						</div>
					</Paper >
				);
			}
			if (isPastDays) {
				specificDays = (
					<Paper className={classes.pastDays}>
						<Grid item > {new Date(day).getDate()}</Grid>
					</Paper>
				);
			}
		} else {
			specificDays = (<Paper className={classes.notInThisMonthDayPaper}>
				<Grid item style={{ display: 'none' }}>
					{new Date(day).getDate()}
				</Grid>
			</Paper>
			);
		}
		return specificDays;
	}

	return (
		<MuiPickersUtilsProvider utils={MomentUtils} locale={locale}>
			<div ref={anchorElRef} className={`${classes.container}  ${boxes ? classes.boxes : classes.pickers}`}> {/*ref using to fix mui bug https://github.com/mui/material-ui-pickers/issues/2022 */}
				<DatePicker
					disableToolbar
					variant="inline"
					placeholder={tr('Check in')}
					format={boxes ? 'MMM-DD' : 'YYYY-MM-DD'}
					value={dates.from}
					autoOk={true}
					minDate={moment(reservationDates.from)}
					maxDate={moment(reservationDates.to).subtract(reservationDates.minDuration, 'days')}
					TextFieldComponent={(props) => DateComponent(props, boxes)}
					onChange={(date) => handleDateChange('from', date)}
					onClose={handleDateClose}
					onOpen={() => handleDateOpen('from')}
					renderDay={(day, selectedDate, isInCurrentMonth) => getDayElement(day, dates.from, isInCurrentMonth, 'checkIn')}
					onMonthChange={(date) => handleMonthChange('from', date)}
					PopoverProps={{
						anchorEl: () => anchorEl ? anchorEl.current : '',
					}}
				/>


				{!boxes &&
					<span className={classes.separator} />
				}
				<DatePicker
					disableToolbar
					variant="inline"
					placeholder={tr('Check Out')}
					format={boxes ? 'MMM-DD' : 'YYYY-MM-DD'}
					value={dates.to}
					autoOk={true}
					minDate={moment(reservationDates.from).add(reservationDates.minDuration, 'days')}
					maxDate={moment(reservationDates.to)}
					TextFieldComponent={(props) => DateComponent(props, boxes)}
					onChange={(date) => handleDateChange('to', date)}
					onClose={handleDateClose}
					onOpen={() => handleDateOpen('to')}
					renderDay={(day, selectedDate, isInCurrentMonth) => getDayElement(day, dates.to, isInCurrentMonth, 'checkOut')}
					onMonthChange={(date) => handleMonthChange('to', date)}
					PopoverProps={{
						anchorEl: () => anchorEl ? anchorEl.current : '',
					}}
				/>
			</div>
		</MuiPickersUtilsProvider >
	);
}

DatePickers.propTypes = {
	dates: PropTypes.object.isRequired,
	setDates: PropTypes.func.isRequired,
	boxes: PropTypes.bool,
	productId: PropTypes.string,
	minDuration: PropTypes.number,
};

export default DatePickers;

const useStyles = makeStyles(theme => ({
	container: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	pickers: {
		padding: '3px 8px',
		border: `1px solid ${theme.palette.custom.borders}`,
		borderRadius: '4px',

		[theme.breakpoints.down('sm')]: {
			padding: '2px 0',
			marginBottom: theme.spacing(2),
		},
	},
	input: {
		border: 0,
		width: '128px',
		padding: '4px 0',
		fontFamily: 'inherit',
		color: theme.palette.primary.dark,
		'& input': {
			textAlign: 'center',
			fontSize: '16px',
		},
		[theme.breakpoints.down('sm')]: {
			width: '110px',
		},
	},
	separator: {
		width: '14px',
		height: '1px',
		backgroundColor: theme.palette.custom.separator,

		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},

	label: {
		fontWeight: 600,
	},
	box: {
		textAlign: 'center',
		padding: '1px 16px',
		border: `1px solid ${theme.palette.custom.borders}`,
		borderRadius: '4px',
		lineHeight: '1.4rem',
		marginLeft: '8px',
		cursor: 'pointer',

		[theme.breakpoints.up('sm')]: {
			padding: '1px 32px',
		},
		[theme.breakpoints.up('md')]: {
			padding: '1px 48px',
		},

		'&:hover': {
			borderColor: theme.palette.custom.main,
		},
	},
	date: {
		color: theme.palette.primary.dark,
		width: '70px',
		height: '22px',
		'& input': {
			textAlign: 'center',
			fontSize: '16px',
			cursor: 'pointer',
		},
	},
	notInThisMonthDayPaper: {
		width: '39.5px',
		height: '39.5px',
		boxShadow: 'none',
		padding: '1px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	pastDays: {
		width: '39.5px',
		height: '39.5px',
		boxShadow: 'none',
		padding: '1px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		color: '#aeaeae',
	},
	normalDayPaper: {
		width: '39.5px',
		height: '39.5px',
		boxShadow: 'none',
		borderRadius: 0,
		padding: '1px',
		cursor: 'pointer',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	selectedDayPaper: {
		width: '39.5px',
		height: '39.5px',
		backgroundColor: '#006CFF',
		color: 'white',
		boxShadow: 'none',
		borderRadius: '50%',
		border: '2px solid #fff',
		padding: '1px',
		cursor: 'pointer',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	todayPaper: {
		width: '39.5px',
		height: '39.5px',
		boxShadow: 'none',
		padding: '1px',
		cursor: 'pointer',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: '50%',
	},
	specificDaysTodayPaper: {
		width: '39.5px',
		height: '39.5px',
		color: '#aeaeae',
		boxShadow: 'none',
		padding: '1px',
		cursor: 'pointer',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: 0,
	},
	specificDaysNormalDayPaper: {
		width: '39.5px',
		height: '39.5px',
		boxShadow: 'none',
		borderRadius: 0,
		padding: '1px',
		cursor: 'pointer',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		color: '#aeaeae',
	},
	daysHover: {
		width: '36px',
		height: '36px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'&:hover': {
			borderRadius: '50%',
			border: '2px solid #006CFF',
		},
	},
	specificSelectedDayPaper: {
		width: '39.5px',
		height: '39.5px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
}));
